/**
 * Terminal session actions for the surfaces that host shells.
 *
 * Three hosts share one guarded close: the bottom drawer, the right panel's
 * Terminal tab, and ChatView's terminal controls. A runtime without a `close`
 * RPC still gets an exit written to it, and the final session's history is
 * cleared rather than left behind, whatever surface the close came from.
 * Deriving that three times is how the hosts drift, so the guarded close
 * lives here once as plain functions and every host supplies its own
 * surrounds (focus bumps, drawer visibility, tab closing).
 */
import { type ScopedThreadRef, type ThreadId } from "@cmmd-forge/contracts";

import { readEnvironmentApi } from "~/environmentApi";
import { randomUUID } from "~/lib/utils";
import { useTerminalStateStore } from "~/terminalStateStore";

/**
 * Close one terminal session, guarded. Returns false when no runtime API can
 * act, so the caller knows not to touch its own store state for a close that
 * never began.
 */
export function closeTerminalSessionGuarded(input: {
  readonly threadRef: ScopedThreadRef;
  readonly threadId: ThreadId;
  readonly terminalId: string;
  readonly isFinalSession: boolean;
}): boolean {
  const api = readEnvironmentApi(input.threadRef.environmentId);
  if (!api) return false;
  /**
   * Writes `exit` to the session when no close RPC exists.
   */
  const fallbackExitWrite = () =>
    api.terminal
      .write({ threadId: input.threadId, terminalId: input.terminalId, data: "exit\n" })
      .catch(() => undefined);

  if ("close" in api.terminal && typeof api.terminal.close === "function") {
    void (async () => {
      if (input.isFinalSession) {
        await api.terminal
          .clear({ threadId: input.threadId, terminalId: input.terminalId })
          .catch(() => undefined);
      }
      await api.terminal.close({
        threadId: input.threadId,
        terminalId: input.terminalId,
        deleteHistory: true,
      });
    })().catch(() => fallbackExitWrite());
  } else {
    void fallbackExitWrite();
  }
  return true;
}

/**
 * Close every session on the thread. Unlike the single close there is no
 * false case: a missing runtime still leaves the writes as no-ops and the
 * caller clears its own store state either way.
 */
function closeAllTerminalSessionsGuarded(input: {
  readonly threadRef: ScopedThreadRef;
  readonly threadId: ThreadId;
  readonly sessionIds: ReadonlyArray<string>;
}): void {
  const api = readEnvironmentApi(input.threadRef.environmentId);

  /**
   * Writes `exit` to every session when no close RPC exists.
   */
  const fallbackExitWrites = () =>
    Promise.all(
      input.sessionIds.map((terminalId) => {
        if (!api) return Promise.resolve(undefined);
        return api.terminal
          .write({ threadId: input.threadId, terminalId, data: "exit\n" })
          .catch(() => undefined);
      }),
    ).then(() => undefined);

  if (api) {
    if ("close" in api.terminal && typeof api.terminal.close === "function") {
      void api.terminal
        .close({ threadId: input.threadId, deleteHistory: true })
        .catch(() => fallbackExitWrites());
    } else {
      void fallbackExitWrites();
    }
  } else {
    void fallbackExitWrites();
  }
}

export interface ThreadTerminalSessionControls {
  readonly splitTerminal: () => void;
  readonly createTerminal: () => void;
  readonly activateTerminal: (terminalId: string) => void;
  readonly closeTerminal: (terminalId: string) => void;
  readonly closeAllTerminals: () => void;
}

/**
 * The session actions one hosting surface hands to its UI: split, create,
 * activate, close, and close all. Every action invokes the caller's
 * after-action callback; the close actions also use guarded close and invoke
 * the final-session callback.
 */
export function useThreadTerminalSessionControls(input: {
  readonly threadRef: ScopedThreadRef;
  readonly threadId: ThreadId;
  /** The thread's session ids as the calling surface sees them. */
  readonly sessionIds: ReadonlyArray<string>;
  /**
   * Whether creating a session may open the drawer. The drawer itself wants
   * that; the panel's Terminal tab must not pop the drawer open uninvited.
   */
  readonly opensDrawerOnCreate?: boolean;
  /** Runs after every action; the drawer bumps its focus request here. */
  readonly onAfterAction?: () => void;
  /** Runs when an action closed the thread's final session. */
  readonly onCloseFinalSession?: () => void;
}): ThreadTerminalSessionControls {
  const { threadRef, threadId, sessionIds, onAfterAction, onCloseFinalSession } = input;

  /**
   * Adds another session to the thread's terminal group.
   */
  const splitTerminal = () => {
    useTerminalStateStore.getState().splitTerminal(threadRef, `terminal-${randomUUID()}`);
    onAfterAction?.();
  };

  /**
   * Creates a session, opening the drawer only when the caller asks.
   */
  const createTerminal = () => {
    useTerminalStateStore
      .getState()
      .newTerminal(
        threadRef,
        `terminal-${randomUUID()}`,
        input.opensDrawerOnCreate === true ? { openDrawer: true } : { openDrawer: false },
      );
    onAfterAction?.();
  };

  /**
   * Makes the given session the active one.
   */
  const activateTerminal = (terminalId: string) => {
    useTerminalStateStore.getState().setActiveTerminal(threadRef, terminalId);
    onAfterAction?.();
  };

  /**
   * Closes one session through the guarded close.
   */
  const closeTerminal = (terminalId: string) => {
    const closed = closeTerminalSessionGuarded({
      threadRef,
      threadId,
      terminalId,
      isFinalSession: sessionIds.length <= 1,
    });
    if (!closed) return;

    useTerminalStateStore.getState().closeTerminal(threadRef, terminalId);
    if (sessionIds.length <= 1) onCloseFinalSession?.();
    onAfterAction?.();
  };

  /**
   * Closes every session and clears the thread's terminal state.
   */
  const closeAllTerminals = () => {
    closeAllTerminalSessionsGuarded({ threadRef, threadId, sessionIds });
    useTerminalStateStore.getState().clearTerminalState(threadRef);
    onCloseFinalSession?.();
    onAfterAction?.();
  };

  return { splitTerminal, createTerminal, activateTerminal, closeTerminal, closeAllTerminals };
}
